/* Een Site verwijderen, maar nooit een waar energiemonitoring aan hangt (F9.12).
 *
 * Het EMS van een site is alleen via de sitepagina te openen. Een verwijderde site
 * (deleted_at gezet) maakt dat EMS dus stil onbereikbaar, en herstel kan alleen
 * met SQL. Daarom mag de UI een site alleen verwijderen als VASTSTAAT dat er geen
 * actieve ems.locatie aan hangt. Laden, een fout of geen recht om ems te lezen is
 * geen "geen EMS".
 *
 * Twee lagen:
 *   - emsKenmerkVoor/magVerwijderen: wat de kaart in Locaties toont, uit de ene
 *     vraag voor de hele lijst (haalSiteIdsMetEms).
 *   - verwijderSite: de route zelf. Kijkt vlak vóór het schrijven opnieuw in
 *     ems.locatie voor precies deze site, zodat een verouderde lijst of een
 *     andere aanroeper hem niet omzeilt. Tussen die lezing en het schrijven zit
 *     nog een smal venster; alleen een regel in de database sluit dat (geen
 *     onderdeel van F9.12: geen migratie).
 *
 * Relatieve imports (geen @/), zodat de toetsen dit bestand zonder bundel lezen. */

import { classificeer, haalEmsLocatiesVoorSite, type EmsClient } from '../../ems/crm/data/leesEms';

export type EmsKenmerk =
  | { soort: 'laden' }
  | { soort: 'fout'; melding: string }
  | { soort: 'met-ems' }
  | { soort: 'zonder-ems' };

/** Het kenmerk van één site uit de stand van de vraag "welke sites hebben EMS". */
export function emsKenmerkVoor(
  q: { isPending: boolean; isError: boolean; error?: unknown; data?: Set<string> },
  siteId: string,
): EmsKenmerk {
  if (q.isError) return { soort: 'fout', melding: q.error instanceof Error ? q.error.message : String(q.error) };
  if (q.isPending || !q.data) return { soort: 'laden' };
  return q.data.has(siteId) ? { soort: 'met-ems' } : { soort: 'zonder-ems' };
}

/** Alleen als vaststaat dat er geen actieve energiemonitoring aan de site hangt. */
export function magVerwijderen(kenmerk: EmsKenmerk | null | undefined): boolean {
  return kenmerk?.soort === 'zonder-ems';
}

export const ONTKOPPELEN = 'Ontkoppel eerst de energiemonitoring; dat gaat nu alleen via Greenvision-beheer.';

export type WeigerReden = 'met-ems' | 'onbekend';

export class VerwijderWeigering extends Error {
  reden: WeigerReden;
  constructor(reden: WeigerReden, bericht: string) {
    super(bericht);
    this.name = 'VerwijderWeigering';
    this.reden = reden;
  }
}

/** Het deel van de supabase-js-client dat hier gebruikt wordt. */
export type VerwijderClient = EmsClient & { from(tabel: string): any };

/**
 * Zet deleted_at op de site, nadat ems.locatie opnieuw is gelezen. Weigert met
 * een VerwijderWeigering als er een actieve locatie aan hangt ('met-ems') of als
 * dat niet vast te stellen is ('onbekend'); dan is er niets geschreven.
 */
export async function verwijderSite(client: VerwijderClient, siteId: string): Promise<void> {
  let aantal: number;
  try {
    aantal = (await haalEmsLocatiesVoorSite(client, siteId)).length;
  } catch (e) {
    const melding = e instanceof Error ? e.message : String(e);
    throw new VerwijderWeigering('onbekend',
      `Of er energiemonitoring aan deze locatie hangt kon niet worden gelezen, dus is er niets verwijderd. (${melding})`);
  }
  if (aantal > 0) {
    throw new VerwijderWeigering('met-ems',
      `Aan deze locatie hangt energiemonitoring (${aantal} actieve meetlocatie${aantal === 1 ? '' : 's'}), dus is er niets verwijderd. ${ONTKOPPELEN}`);
  }

  const { error, status } = await client.from('sites').update({ deleted_at: new Date().toISOString() }).eq('id', siteId);
  if (error) throw classificeer(error, status, 'public.sites');
}
